import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, 
  FileText, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Clock,
  Loader2,
  RefreshCw,
  Trash2,
  X,
  FolderUp,
  RotateCcw,
  Eye,
  ChevronLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../App';
import axios from 'axios';
import ImportAlertSettings from '../components/ImportAlertSettings';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ImportVentes() {
  const { token } = useAuth();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processedResults, setProcessedResults] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [selectedTab, setSelectedTab] = useState('all');
  const fileInputRef = useRef(null);

  // Filtered results based on tab
  const filteredResults = useMemo(() => {
    if (!processedResults.length) return [];
    return processedResults.filter(r => {
      if (selectedTab === 'all') return true;
      if (selectedTab === 'success') return r.success;
      if (selectedTab === 'failed') return !r.success && !r.duplicate;
      if (selectedTab === 'duplicate') return r.duplicate;
      return true;
    });
  }, [processedResults, selectedTab]);

  // Handle file selection
  const handleFilesSelected = (selectedFiles) => {
    const pdfFiles = Array.from(selectedFiles).filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (pdfFiles.length === 0) {
      toast.error('Aucun fichier PDF sélectionné');
      return;
    }
    setFiles(prev => [...prev, ...pdfFiles]);
  };

  // Remove a file from the list
  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Clear all files
  const clearFiles = () => {
    setFiles([]);
    setProcessedResults([]);
  };

  // Start batch upload with OCR
  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error('Aucun fichier à importer');
      return;
    }

    setUploading(true);
    setProcessing(true);
    setUploadProgress(0);
    setProcessedResults([]);

    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const res = await axios.post(`${API}/api/invoices/upload-batch-ocr`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
        timeout: 600000 // 10 minutes for large batches
      });

      setProcessedResults(res.data.results || []);
      
      const { successful, failed, total } = res.data;
      if (successful === total) {
        toast.success(`${successful} facture(s) importée(s) avec succès`);
      } else {
        toast.info(`${successful}/${total} factures importées. ${failed} échec(s).`);
      }
      
      // Clear the files queue
      setFiles([]);
      
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setUploading(false);
      setProcessing(false);
      setUploadProgress(0);
    }
  };

  const getStatusBadge = (result) => {
    if (result.success) {
      return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Importé</Badge>;
    }
    if (result.duplicate) {
      return <Badge className="bg-amber-500"><AlertCircle className="w-3 h-3 mr-1" />Doublon</Badge>;
    }
    return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Échec</Badge>;
  };

  const successCount = processedResults.filter(r => r.success).length;
  const failedCount = processedResults.filter(r => !r.success && !r.duplicate).length;
  const duplicateCount = processedResults.filter(r => r.duplicate).length;

  return (
    <div className="p-6 space-y-6" data-testid="import-ventes-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FolderUp className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Import en masse - Ventes</h1>
            <p className="text-muted-foreground">Importez plusieurs factures clients simultanément via OCR</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ImportAlertSettings module="ventes" />
          {files.length > 0 && !processing && (
            <Button variant="outline" onClick={clearFiles}>
              <Trash2 className="w-4 h-4 mr-2" />
              Tout effacer
            </Button>
          )}
        </div>
      </div>

      {/* Upload Zone */}
      <Card>
        <CardContent className="p-0">
          <div 
            className={`
              border-2 border-dashed rounded-lg p-12 text-center transition-all cursor-pointer
              ${dragOver ? 'border-primary bg-primary/10' : 'border-gray-300 hover:border-primary hover:bg-primary/5'}
              ${uploading || processing ? 'pointer-events-none opacity-50' : ''}
            `}
            onClick={() => !uploading && !processing && fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              handleFilesSelected(e.dataTransfer.files);
            }}
            data-testid="upload-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf"
              className="hidden"
              onChange={(e) => handleFilesSelected(e.target.files)}
              disabled={uploading || processing}
            />
            
            {processing ? (
              <div className="space-y-4">
                <Loader2 className="w-16 h-16 mx-auto text-primary animate-spin" />
                <div>
                  <p className="text-xl font-semibold">Traitement OCR en cours...</p>
                  <p className="text-muted-foreground">Extraction des données, veuillez patienter</p>
                </div>
                <Progress value={uploadProgress} className="w-64 mx-auto h-2" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="w-20 h-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                  <Upload className="w-10 h-10 text-primary" />
                </div>
                <div>
                  <p className="text-xl font-semibold">Glissez vos fichiers PDF ici</p>
                  <p className="text-muted-foreground">ou cliquez pour sélectionner</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  Factures clients au format PDF
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Files Queue */}
      {files.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                Fichiers en attente ({files.length})
              </CardTitle>
              <Button 
                onClick={handleUpload} 
                disabled={uploading || processing}
                data-testid="start-import-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Traitement...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Lancer l'import OCR
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="max-h-64 overflow-y-auto border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Fichier</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {files.map((file, index) => (
                    <TableRow key={`${file.name}-${index}`}>
                      <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-muted-foreground" />
                          <span className="truncate max-w-xs">{file.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeFile(index)}
                          disabled={uploading || processing}
                          className="text-red-600 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {processedResults.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Résultats de l'import</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="p-4 bg-slate-50 rounded-lg text-center">
                <p className="text-2xl font-bold">{processedResults.length}</p>
                <p className="text-sm text-muted-foreground">Total</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-600">{successCount}</p>
                <p className="text-sm text-green-600">Importés</p>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-amber-600">{duplicateCount}</p>
                <p className="text-sm text-amber-600">Doublons</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-red-600">{failedCount}</p>
                <p className="text-sm text-red-600">Échecs</p>
              </div>
            </div>

            {/* Tabs */}
            <Tabs value={selectedTab} onValueChange={setSelectedTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="all">Tous ({processedResults.length})</TabsTrigger>
                <TabsTrigger value="success" className="text-green-600">Importés ({successCount})</TabsTrigger>
                <TabsTrigger value="duplicate" className="text-amber-600">Doublons ({duplicateCount})</TabsTrigger>
                <TabsTrigger value="failed" className="text-red-600">Échecs ({failedCount})</TabsTrigger>
              </TabsList>

              <div className="max-h-96 overflow-y-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Fichier</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Détail</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredResults.map((result, index) => (
                      <TableRow key={result.filename}>
                        <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-muted-foreground" />
                            <span className="truncate max-w-xs">{result.filename}</span>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(result)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {result.success && result.invoice && (
                            <span className="text-green-600">
                              Facture: {result.invoice.numero} - {result.invoice.client}
                            </span>
                          )}
                          {result.error && (
                            <span className="text-red-600">{result.error}</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Tabs>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
